import SectionCard from "./sectioncard.tsx";
import { Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { fmtDate, fmtDuration } from "../../../utilities/conversions.js";
import Swal from "sweetalert2";
import { RootState } from "../../../state/store.ts";
import { useSelector } from "react-redux";

type AppointmentsCutomersProps = {
    baseUrl: string,
    currentUser: any,
}

export default function AppointmentsCustomer({ baseUrl, currentUser }: AppointmentsCutomersProps) {

    const [loading, setLoading] = useState(true);
    const client = useSelector((state: RootState) => state.client.client);
    const default_configs = client ? JSON.parse(client.default_configs) : {};

    var upcomingBookingsSeed = [
        { id: "b1", when: new Date(Date.now() + 86400000).toISOString(), advisor: "Sondre", type: "Telefon", lengthMin: 20 },
        { id: "b2", when: new Date(Date.now() + 172800000).toISOString(), advisor: "Ingrid", type: "Video", lengthMin: 30 },
    ];

    const fetchUpcomingBookings = async () => {
        setLoading(true);
        try {
            await axios.get(baseUrl + '/getUpcommingBookings', {
                params: { token: currentUser.token }
            }).then((response) => {
                if (response.data.status === "OK") {
                    setUpcoming(response.data.data);
                }
            })
        } catch (err) {
            console.error("Error fetching upcoming bookings:", err);
        } finally {
            setLoading(false);
        }


    }

    const handleCancelBooking = async (booking_id: string) => {

        // ✅ Await the Swal.fire() promise
        const result = await Swal.fire({
            title: "Avbestill konsultasjon",
            html: `
                <h3 style="margin-bottom: 2rem !important">Er du sikker på at du vil avbestille denne konsultasjonen?</h3>
                <div style="text-align: left; margin: 0 0 16px; padding: 12px 14px; border-radius: 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-size: 14px; color: #111827;">
                    Når du avbestiller:<br>
                    • Betalingsautorisasjonen annulleres.<br>
                    • <strong>No-show-gebyr dersom avbestilling mindre enn 24 timer før</strong>.
                </div>
                <div style="margin-top: 3rem;text-align: left;font-weight: 600;font-size: 14px; color: #4f46e5">
                    <a href="/vilkar-booking-cancel" style="display: flex" target="_blank" class="modal-terms">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>&nbsp;Les vilkår og betingelser</span>
                    </a>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Avbestill konsultasjon",
            cancelButtonText: "Close",
            customClass: {
                confirmButton: "swal-confirm-btn",
                cancelButton: "swal-cancel-btn",
            },
        });
        // ✅ Now result.isConfirmed is available
        if (result.isConfirmed) {
            try {
                const formData = new FormData();
                formData.append("id", booking_id);
                formData.append("token", currentUser.token);

                const response = await axios.post(baseUrl + "/deleteEvent", formData);

                if (response.data.status === "OK") {
                    Swal.fire({
                        icon: "success",
                        title: "Avtale avbestilt",
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    fetchUpcomingBookings();
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Feil",
                        text: response.data.message || "Noe gikk galt.",
                    });
                }
            } catch (error) {
                console.error("Error cancelling booking:", error);
                Swal.fire({
                    icon: "error",
                    title: "Feil",
                    text: "Kunne ikke avbestille avtalen. Prøv igjen senere.",
                });
            }
        }


    }

    const [upcoming, setUpcoming] = useState([]);
    const [mode, setMode] = useState(false);

    useEffect(() => {
        fetchUpcomingBookings();
    }, [currentUser, baseUrl])

    const handleJoinClick = (eventKey: string) => {
        window.location.href = window.location.origin + '/customer/join-booking-call/' + eventKey;
    }

    const handleHistorikkAppointments = async () => {
        setLoading(true); // start loading animation

        if (!mode) {
            try {
                const response = await axios.get(baseUrl + '/getCustomerAppointmentsHistory', {
                    params: { token: currentUser?.token }
                });

                if (response.data.status === "OK") {
                    setUpcoming(response.data.data);
                }

                setMode(true);
            } finally {
                setLoading(false);
            }

        } else {
            try {
                setMode(false);
                await fetchUpcomingBookings(); // already sets upcoming
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-600 animate-pulse">Laster inn avtaler...</p>
                </div>
            </div>
        );
    }
    return (
        <SectionCard title="Mine bookede avtaler" icon={<Calendar className="h-5 w-5 text-purple-700" />}>
            <div className="flex border-b mb-4">


                <button
                    onClick={() => {
                        if (mode) handleHistorikkAppointments();
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-all
                        ${!mode
                            ? "border-purple-600 text-purple-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                >
                    Kommede
                </button>
                <button
                    onClick={handleHistorikkAppointments}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-all
                        ${mode
                            ? "border-purple-600 text-purple-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                >
                    Historikk
                </button>
            </div>
            {upcoming.length === 0 ? (
                <div className="text-gray-600">Ingen kommende avtaler.</div>
            ) : (
                <ul className="space-y-3">
                    {upcoming.map((b) => (
                        <li key={b.id} className="rounded-xl border p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                                <div className="font-semibold">{b.advisor}</div>
                                <div className="text-sm text-gray-600 flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmtDate(b.when)} • {b.type} • {fmtDuration(b.lengthMin)}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                {b?.event_key && b.status === "Pending" && default_configs.WebRTC === "yes" && (
                                    <button onClick={() => handleJoinClick(b.event_key)} className="rounded-full bg-purple-600 px-4 py-2 text-white hover:bg-purple-700">Bli med</button>
                                )}
                                {/* <button className="rounded-full border px-4 py-2 hover:border-purple-500">Flytt</button> */}
                                {b.status !== "completed" && b.status !== "Cancelled" && new Date(b.start_date_time.replace(" ", "T")) > new Date()  && (
                                // {b.status !== "completed" && b.status !== "in-progres" && b.status !== "Cancelled" && (
                                    <button onClick={() => handleCancelBooking(b.id)} className="rounded-full border px-4 py-2 hover:border-purple-500">Avbestill</button>
                                )}
                            </div>
                            {b.status === "Cancelled" && (
                                <div className="flex items-center">
                                    <div className="text-sm text-red-600 flex items-center">
                                        Cancelled by {b.cancelled_by}
                                    </div>
                                </div>
                            )}
                            {b.status === "completed" && (
                                <div className="flex items-center">
                                    <div className="text-sm text-green-600 flex items-center">
                                        Completed
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </SectionCard>

    )
}